/**
 * People section (ENTRAID-305): three tabs over one data load.
 *  - People: members table with role chips, row actions, invite or pre-assign.
 *  - Roles: per-role table with expandable members, assign-to-all, Guardian.
 *  - Access requests (operators only): sign-ins awaiting a role.
 * Guardian content is the customer admin's only (operators never read it).
 * Handlers return falsy or handled results and never reject.
 */
import { useCallback, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AUDITOR_ROLE } from '../../../shared/solution.mjs';
import { api } from '../lib/api.js';
import ConsoleTabs, { panelProps } from '../components/ConsoleTabs.jsx';
import Notice from '../components/Notice.jsx';
import PageState from '../components/PageState.jsx';
import { customerSectionPath } from '../lib/navigation.js';
import { accountLabel } from './people/people-format.jsx';
import PeopleTab from './people/PeopleTab.jsx';
import RolesTab from './people/RolesTab.jsx';
import AccessRequestsTab from './people/AccessRequestsTab.jsx';

export { accountLabel };

const AUDITOR_INVITE = Object.freeze([AUDITOR_ROLE]);
const SUBS = { '': 'people', roles: 'roles', 'access-requests': 'access-requests' };

export default function CustomerPeople({ customerId, isOperator = false, operatorAccount = isOperator }) {
  // Watch content is the customer admin's only (ENTRAID-302; the SPA flag is watchOperatorAccount).
  const canGuardian = !operatorAccount;
  const [data, setData] = useState(null);
  const [guardians, setGuardians] = useState(null);
  const [guardiansError, setGuardiansError] = useState(false);
  const [requests, setRequests] = useState([]);
  const [requestsTenantMissing, setRequestsTenantMissing] = useState(false);
  // No verified domains stored for this customer, so no request can be matched to it (PROD 2026-10-27).
  const [requestsDomainsUnknown, setRequestsDomainsUnknown] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [prefillEmail, setPrefillEmail] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  const base = customerSectionPath(customerId, 'people');
  // ?invite=auditor (from the Compliance Schedule tab) opens the invite dialog with the Auditor role preselected.
  const inviteRoles = new URLSearchParams(location.search).get('invite') === 'auditor' ? AUDITOR_INVITE : null;
  const consumeInviteParam = useCallback(() => navigate(base, { replace: true }), [navigate, base]);
  const sub = location.pathname.startsWith(base) ? location.pathname.slice(base.length).replace(/^\/+/, '').replace(/\/+$/, '') : '';

  const load = useCallback(async () => {
    // Members and guardians settle together so the first render already knows whether Guardian is available.
    const [people, guardianRead] = await Promise.allSettled([
      (async () => api.getPeople(customerId))(),
      canGuardian ? (async () => api.listWatchGuardians(customerId))() : Promise.resolve(null),
    ]);
    if (people.status === 'fulfilled') {
      setData(people.value);
      setError(null);
    } else {
      setError(people.reason?.message ?? 'Could not load people');
    }
    if (!canGuardian) {
      setGuardians(null);
      setGuardiansError(false);
    } else if (guardianRead.status === 'fulfilled') {
      setGuardians(guardianRead.value?.guardians ?? []);
      setGuardiansError(false);
    } else {
      setGuardians(null);
      setGuardiansError(true);
    }
    // Access requests are operators only (security review L3); when they cannot be read, the tab is simply empty.
    if (isOperator) {
      try {
        const r = await api.getAccessRequests(customerId);
        setRequests(Array.isArray(r?.requests) ? r.requests : []);
        // The customer's IdP tenant does not exist yet (ENTRAID-307): nobody was invited, said as such.
        setRequestsTenantMissing(r?.tenantMissing === true);
        setRequestsDomainsUnknown(r?.domainsUnknown === true);
      } catch {
        setRequests([]);
        setRequestsTenantMissing(false);
        setRequestsDomainsUnknown(false);
      }
    } else {
      setRequests([]);
      setRequestsTenantMissing(false);
      setRequestsDomainsUnknown(false);
    }
  }, [customerId, canGuardian, isOperator]);

  useEffect(() => { setData(null); setError(null); setNotice(null); load(); }, [load]);

  async function run(action, ok) {
    setNotice(null);
    try {
      const out = await action();
      if (ok) setNotice({ tone: 'ok', text: ok });
      await load();
      return out ?? true;
    } catch (e) {
      setNotice({ tone: 'error', text: e.message });
      return false;
    }
  }

  // Guardian is stored in the Watch settings, not on the IdP: it is added after the invite through the same Watch
  // route Manage roles uses, so it meets the same checks. Two services, not atomic: a Guardian failure after a
  // sent invite is reported as such and the person stays invited.
  const onInvite = async (email, roles, staff = false, expiresAt = undefined, guardian = false) => {
    // Guardian alone (no portal role): nothing to invite or pre-assign, only the Watch guardian entry. Same Watch route,
    // same admin-only checks; the invite endpoint (which needs at least one portal role) is not called.
    if (guardian && roles.length === 0) {
      return run(() => api.addWatchGuardian(customerId, email), `${email} was added as a Guardian. The collector checks the address in your directory within 15 minutes.`);
    }
    const okText = staff ? `Roles pre-assigned to ${email}. They apply at their next IPC Global sign-in. No email was sent.` : `Invitation sent to ${email}.`;
    const invited = await run(() => (expiresAt === undefined ? api.invitePerson(customerId, email, roles) : api.invitePerson(customerId, email, roles, expiresAt)), guardian ? null : okText);
    if (!guardian || !invited) return invited;
    try {
      await api.addWatchGuardian(customerId, email);
      setNotice({ tone: 'ok', text: `${okText} They were added as a Guardian.` });
    } catch (e) {
      setNotice({ tone: 'error', text: `${okText} Guardian was not added: ${e?.message || 'The Guardian change could not be saved.'}` });
    }
    await load();
    return invited;
  };
  const onUpdate = (email, roles, expectedRoles) => run(
    () => (expectedRoles === undefined ? api.updatePersonRoles(customerId, email, roles) : api.updatePersonRoles(customerId, email, roles, expectedRoles)),
    `Roles updated for ${email}. The change applies at their next sign-in.`,
  );
  const onRevoke = (email) => run(() => api.revokePerson(customerId, email), `Access revoked for ${email}. An open session ends when its sign-in token expires.`);
  const onDismiss = async (email) => {
    setNotice(null);
    try {
      await api.dismissAccessRequest(email);
      setRequests((rows) => rows.filter((r) => r.email !== email));
    } catch (e) {
      setNotice({ tone: 'error', text: e.message });
    }
  };
  // One call grants the role to every member (ENTRAID-296); the Roles tab shows the counts.
  const onAssignAll = async (role) => {
    setNotice(null);
    try {
      const out = await api.assignRoleToAll(customerId, role);
      await load();
      return out;
    } catch (e) {
      setNotice({ tone: 'error', text: e.message });
      return null;
    }
  };

  if (error) return <PageState state="error" title="People could not be loaded" message={error} onRetry={load} />;
  if (!data) return <PageState state="loading" header={false} cards={0} label="Loading people" />;

  const tabs = [
    { id: 'people', label: 'People' },
    { id: 'roles', label: 'Roles' },
    ...(isOperator ? [{ id: 'access-requests', label: 'Access requests', count: requests.length }] : []),
  ];
  const active = SUBS[sub];
  if (!active || !tabs.some((t) => t.id === active)) return <Navigate to={base} replace />;
  const go = (id) => navigate(id === 'people' ? base : `${base}/${id}`);
  // When the guardian read failed, no Guardian control or Directory row is shown, so nothing can be saved blind.
  const guardianOk = canGuardian && !guardiansError;

  return (
    <div className="space-y-4">
      {notice ? (
        <Notice tone={notice.tone === 'error' ? 'bad' : 'good'} role={notice.tone === 'error' ? 'alert' : 'status'} titleAs="p" title={notice.text} />
      ) : null}
      {guardiansError ? (
        <Notice tone="warn" role="status" titleAs="p" title="Guardians could not be loaded">
          The Guardian role is not shown until they load. Reload to try again.
        </Notice>
      ) : null}
      <ConsoleTabs label="People" idPrefix="people" items={tabs} value={active} onChange={go} />
      <div {...panelProps('people', active)}>
        {active === 'people' ? (
          <PeopleTab
            customerId={customerId}
            data={data}
            guardians={guardians}
            canGuardian={guardianOk}
            isOperator={isOperator}
            resendInvite={data.capabilities?.resendInvite === true}
            onInvite={onInvite}
            onRevoke={onRevoke}
            onChanged={load}
            prefillEmail={prefillEmail}
            inviteRoles={inviteRoles}
            onInviteRolesConsumed={consumeInviteParam}
            onPrefillConsumed={() => setPrefillEmail('')}
          />
        ) : null}
        {active === 'roles' ? (
          <RolesTab customerId={customerId} data={data} guardians={guardians} canGuardian={guardianOk} onUpdate={onUpdate} onRevoke={onRevoke} onAssignAll={onAssignAll} onChanged={load} />
        ) : null}
        {active === 'access-requests' ? (
          <AccessRequestsTab requests={requests} tenantMissing={requestsTenantMissing} domainsUnknown={requestsDomainsUnknown} onPreAssign={(email) => { setPrefillEmail(email); navigate(base); }} onDismiss={onDismiss} />
        ) : null}
      </div>
    </div>
  );
}
